import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiBriefcase,
  FiDollarSign,
  FiTrendingUp,
  FiCheckCircle,
  FiRefreshCw,
  FiBarChart,
  FiLayers,
  FiSettings,
  FiArrowRight,
  FiActivity,
  FiClock,
  FiStar,
} from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import {
  AdminPageHeader,
  AdminKpiGrid,
  AdminEmpty,
  pillClass,
  formatArs,
  formatAdminActionLabel,
  adminTimeAgo,
} from '../components/admin';
import { FormAlert } from '../components/form';
import { useApi } from '../hooks/useApi';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';

const formatActionLabel = formatAdminActionLabel;

const timeAgo = adminTimeAgo;

const activityIcon = (action = '') => {
  const value = String(action).toLowerCase();
  if (value.includes('user') || value.includes('usuario') || value.includes('team')) return FiUsers;
  if (value.includes('company') || value.includes('empresa')) return FiBriefcase;
  if (value.includes('plan')) return FiLayers;
  return FiActivity;
};

const EMPTY_STATS = {
  totalUsers: 0,
  totalCompanies: 0,
  totalPlans: 0,
  platformMrr: 0,
  activeSubscriptions: 0,
  activeCompanySubscriptions: 0,
  activeUserSubscriptions: 0,
  systemHealth: 'healthy',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const {
    canManageUsers,
    canManageCompanies,
    canManagePlans,
    canManageSystem,
    canAccessABM,
    isSuperAdmin,
    loading: permissionsLoading,
    error: permissionsError,
  } = useAdminPermissions();

  const [stats, setStats] = useState(EMPTY_STATS);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [betaProgram, setBetaProgram] = useState(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      setError('');
      const requests = [
        secureFetch('/api/admin/dashboard/stats'),
        secureFetch('/api/admin/dashboard/activities'),
      ];
      if (canManageSystem()) {
        requests.push(secureFetch('/api/admin/beta-program'));
      }
      const [statsRes, activitiesRes, betaRes] = await Promise.all(requests);

      if (statsRes.ok) {
        setStats({ ...EMPTY_STATS, ...(await statsRes.json()) });
      } else {
        throw new Error('No se pudieron cargar las métricas.');
      }

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setRecentActivities((activitiesData || []).slice(0, 12));
      }

      if (betaRes?.ok) {
        setBetaProgram(await betaRes.json());
      } else if (!canManageSystem()) {
        setBetaProgram(null);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
      setError(err.message || 'No se pudo actualizar el panel.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [secureFetch, canManageSystem]);

  useEffect(() => {
    if (canAccessABM()) loadDashboardData();
  }, [canAccessABM, loadDashboardData]);

  const superAdmin = isSuperAdmin();
  const healthTone = {
    healthy: 'success',
    warning: 'warning',
    critical: 'danger',
  }[stats.systemHealth] || 'success';
  const healthLabel = {
    healthy: 'Operativo',
    warning: 'Advertencia',
    critical: 'Crítico',
  }[stats.systemHealth] || 'Operativo';

  const kpis = [
    {
      icon: FiUsers,
      value: stats.totalUsers.toLocaleString('es-AR'),
      label: 'Usuarios',
      hint: `${stats.activeUserSubscriptions} con plan activo`,
      onClick: canManageUsers() ? () => navigate('/admin/users') : undefined,
    },
    {
      icon: FiBriefcase,
      value: stats.totalCompanies.toLocaleString('es-AR'),
      label: 'Empresas',
      hint: `${stats.activeCompanySubscriptions} con suscripción activa`,
      onClick: canManageCompanies() ? () => navigate('/admin/companies') : undefined,
    },
    {
      icon: FiLayers,
      value: stats.activeSubscriptions.toLocaleString('es-AR'),
      label: 'Suscripciones',
      hint: `${stats.totalPlans} planes publicados`,
      onClick: canManagePlans() ? () => navigate('/admin/plans') : undefined,
    },
    {
      icon: FiDollarSign,
      value: formatArs(stats.platformMrr, { compact: true }),
      label: 'MRR estimado',
      hint: 'Ingresos mensuales de planes activos',
      onClick: canManagePlans() ? () => navigate('/admin/plans') : undefined,
    },
  ];

  const platformRows = [
    { label: 'Usuarios con plan activo', value: stats.activeUserSubscriptions },
    { label: 'Empresas con suscripción', value: stats.activeCompanySubscriptions },
    { label: 'Planes publicados', value: stats.totalPlans },
    { label: 'Suscripciones activas', value: stats.activeSubscriptions },
  ];

  const quickLinks = [
    canManageUsers() && { icon: FiUsers, title: 'Usuarios', desc: 'Alta, edición y roles', path: '/admin/users' },
    canManageCompanies() && { icon: FiBriefcase, title: 'Empresas', desc: 'Organizaciones y planes', path: '/admin/companies' },
    canManagePlans() && { icon: FiLayers, title: 'Planes', desc: 'Precios y límites', path: '/admin/plans' },
    canManageSystem() && { icon: FiStar, title: 'Programa beta', desc: 'Testers, plazos y avisos', path: '/admin/beta' },
    canManageSystem() && { icon: FiSettings, title: 'Sistema', desc: 'Estado y correo', path: '/admin/system' },
    superAdmin && { icon: FiBarChart, title: 'Parametría', desc: 'Escalas de Monotributo', path: '/admin/fiscal' },
  ].filter(Boolean);

  if (!permissionsLoading && !canAccessABM()) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className={`rounded-2xl border p-6 max-w-md text-center ${tc.box}`}>
            <h3 className={`text-base font-semibold mb-2 ${tc.textPri}`}>Acceso restringido</h3>
            <p className={`mb-4 text-sm ${tc.textSec}`}>No tenés permisos para acceder al panel de administración.</p>
            <button type="button" onClick={() => navigate(-1)} className={`px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>
              Volver
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (permissionsError) {
    return (
      <AdminLayout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className={`rounded-2xl border p-6 max-w-md text-center ${tc.box}`}>
            <p className={`mb-3 text-sm ${tc.textSec}`}>{permissionsError}</p>
            <button type="button" onClick={() => window.location.reload()} className={`px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>
              Reintentar
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto py-6 space-y-5">
        <AdminPageHeader
          title="Panel del sistema"
          subtitle={
            superAdmin
              ? 'Vista global de usuarios, empresas, suscripciones y actividad.'
              : 'Resumen operativo de tu organización.'
          }
          actions={
            <>
              <span className={pillClass(tc.modoOscuro, healthTone)}>
                <FiCheckCircle className="w-3.5 h-3.5" />
                {healthLabel}
              </span>
              {lastUpdated && (
                <span className={`hidden sm:inline text-[11px] ${tc.textMuted}`}>
                  <FiClock className="inline w-3 h-3 mr-1" />
                  {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                type="button"
                onClick={loadDashboardData}
                disabled={refreshing}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50 ${tc.btnOutline}`}
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </>
          }
        />

        {error && <FormAlert type="error">{error}</FormAlert>}

        {permissionsLoading || loading ? (
          <div className={`rounded-2xl border p-10 text-center ${tc.box}`}>
            <div className="w-9 h-9 border-[3px] border-gray-300 border-t-[#1F80FF] rounded-full animate-spin mx-auto mb-3" />
            <p className={`text-sm ${tc.textSec}`}>Cargando panel…</p>
          </div>
        ) : (
          <>
            <AdminKpiGrid items={kpis} />

            {canManageSystem() && betaProgram && (
              <section className={`rounded-2xl border p-4 sm:p-5 ${tc.box}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className={`text-sm font-semibold flex items-center gap-2 ${tc.textPri}`}>
                      <FiStar className="w-4 h-4 text-[#1F80FF]" />
                      Usuarios en beta
                    </h2>
                    <p className={`text-xs mt-1 ${tc.textMuted}`}>
                      Plan {betaProgram.planName || 'Profesional'}
                      {betaProgram.planMonthlyPriceArs != null
                        ? ` · suscripción ${formatArs(betaProgram.planMonthlyPriceArs)}/mes`
                        : ''}
                      {betaProgram.freeDays ? ` · ${betaProgram.freeDays} días gratis` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/admin/beta')}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium ${tc.textSec} hover:text-[#1F80FF]`}
                  >
                    Ver programa completo
                    <FiArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {[
                    { label: 'Cupos', value: `${betaProgram.granted ?? 0}/${betaProgram.limit ?? 0}` },
                    { label: 'Por vencer', value: betaProgram.summary?.endingSoon ?? 0 },
                    { label: 'Sin aviso 7d', value: betaProgram.summary?.missingReminder7d ?? 0 },
                    { label: 'Suscritos', value: betaProgram.summary?.converted ?? 0 },
                  ].map((item) => (
                    <div key={item.label} className={`rounded-xl p-2.5 ${tc.section}`}>
                      <p className={`text-[10px] uppercase tracking-wide ${tc.textMuted}`}>{item.label}</p>
                      <p className={`text-lg font-semibold tabular-nums ${tc.textPri}`}>{item.value}</p>
                    </div>
                  ))}
                </div>

                {(betaProgram.users || []).length > 0 ? (
                  <ul className="space-y-2">
                    {[...(betaProgram.users || [])]
                      .sort((a, b) => {
                        if (Boolean(a.needsAttention) !== Boolean(b.needsAttention)) {
                          return a.needsAttention ? -1 : 1;
                        }
                        const da = a.daysRemaining ?? 9999;
                        const db = b.daysRemaining ?? 9999;
                        return da - db;
                      })
                      .slice(0, 5)
                      .map((row) => (
                        <li
                          key={row.userId}
                          className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 ${tc.section}`}
                        >
                          <div className="min-w-0">
                            <p className={`text-xs font-medium truncate ${tc.textPri}`}>{row.name}</p>
                            <p className={`text-[10px] truncate ${tc.textMuted}`}>
                              {row.daysRemaining != null
                                ? row.daysRemaining >= 0
                                  ? `${row.daysRemaining}d restantes`
                                  : `Venció hace ${Math.abs(row.daysRemaining)}d`
                                : 'Sin fecha'}
                              {row.betaProEndsAt
                                ? ` · ${new Date(row.betaProEndsAt).toLocaleDateString('es-AR')}`
                                : ''}
                            </p>
                          </div>
                          <span
                            className={pillClass(
                              tc.modoOscuro,
                              row.converted
                                ? 'success'
                                : row.phase === 'ending_soon'
                                  ? 'warning'
                                  : row.phase === 'ended'
                                    ? 'danger'
                                    : 'neutral'
                            )}
                          >
                            {row.converted
                              ? 'MP activo'
                              : row.missingCriticalReminder
                                ? 'Sin aviso 7d'
                                : row.phase === 'ending_soon'
                                  ? 'Por vencer'
                                  : row.subscriptionStatus || '—'}
                          </span>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <AdminEmpty
                    icon={FiStar}
                    title="Aún no hay usuarios en beta"
                    subtitle={
                      betaProgram.enabled
                        ? `Quedan ${betaProgram.remaining ?? 0} cupos del programa de lanzamiento.`
                        : 'Activá BETA_PRO_SIGNUP_ENABLED en el servidor para ofrecer cupos.'
                    }
                  />
                )}
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <section className={`rounded-2xl p-4 border ${tc.box}`}>
                <h2 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${tc.textPri}`}>
                  <FiTrendingUp className="w-4 h-4" />
                  Plataforma
                </h2>
                <div className="space-y-2">
                  {platformRows.map((row) => (
                    <div key={row.label} className={`flex items-start justify-between gap-3 p-2.5 rounded-lg ${tc.section}`}>
                      <div>
                        <span className={`text-xs ${tc.textSec}`}>{row.label}</span>
                        {row.note && <p className={`text-[10px] mt-0.5 ${tc.textMuted}`}>{row.note}</p>}
                      </div>
                      <span className={`text-xs font-semibold tabular-nums ${tc.textPri}`}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className={`lg:col-span-2 rounded-2xl p-4 border ${tc.box}`}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className={`text-sm font-semibold flex items-center gap-2 ${tc.textPri}`}>
                    <FiActivity className="w-4 h-4" />
                    Actividad reciente
                  </h2>
                  {canManageSystem() && (
                    <button
                      type="button"
                      onClick={() => navigate('/admin/activity')}
                      className={`text-[11px] inline-flex items-center gap-1 ${tc.textMuted} hover:text-[#1F80FF]`}
                    >
                      Ver todo
                      <FiArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => {
                      const Icon = activityIcon(activity.action);
                      return (
                        <div key={`${activity.createdAt}-${index}`} className={`flex items-start gap-3 p-3 rounded-lg ${tc.section}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white ${tc.modoOscuro ? 'bg-gray-700' : 'bg-gray-900'}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${tc.textPri}`}>{formatActionLabel(activity.action)}</p>
                            {activity.details && (
                              <p className={`text-xs mt-0.5 truncate ${tc.textMuted}`}>{activity.details}</p>
                            )}
                            <p className={`text-[11px] mt-1 ${tc.textMuted}`}>
                              {activity.user} · {timeAgo(activity.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <AdminEmpty icon={FiActivity} title="No hay actividad reciente" subtitle="Los cambios de usuarios, empresas y planes van a aparecer acá." />
                  )}
                </div>
              </section>
            </div>

            {quickLinks.length > 0 && (
              <section className={`rounded-2xl p-4 border ${tc.box}`}>
                <h2 className={`text-sm font-semibold mb-3 ${tc.textPri}`}>Accesos rápidos</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {quickLinks.map(({ icon: Icon, title, desc, path }) => (
                    <button
                      key={path}
                      type="button"
                      onClick={() => navigate(path)}
                      className={`p-4 rounded-xl border text-left transition-colors group ${tc.modoOscuro ? 'bg-gray-800/70 hover:bg-gray-800 border-gray-700' : 'bg-gray-50 hover:bg-white border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${tc.modoOscuro ? 'bg-gray-700' : 'bg-gray-900'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <FiArrowRight className={`w-4 h-4 ${tc.textMuted} group-hover:text-[#1F80FF]`} />
                      </div>
                      <div className={`font-medium text-sm ${tc.textPri}`}>{title}</div>
                      <div className={`text-xs mt-0.5 ${tc.textSec}`}>{desc}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
